import { useMemo } from "react";
import type { IAtributo } from "interfaces/loja/atributosProduto";
import type { IImagemProduto } from "interfaces/loja/produtos";
import type { IVariacaoProdutoState } from "interfaces/loja/variacaoProduto";
import { LojaDataGrid } from "../../../common";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { GridColDef } from "@mui/x-data-grid";
import Switch from "@mui/material/Switch";
import Radio from "@mui/material/Radio";
import Chip from "@mui/material/Chip";
import { formatCurrency } from "helpers/utils";
import { getLabelAtributoVariacao } from "./helpers";

const MAX_THUMBNAILS = 3;
const THUMB_SIZE = 30;

export type VariacaoRowData = IVariacaoProdutoState & {
  id: string;
  _index: number;
  imagens: IImagemProduto[];
};

export interface VariacoesTableProps {
  variacoes: IVariacaoProdutoState[];
  atributos: IAtributo[];
  imagensProduto: IImagemProduto[];
  onEditar: (index: number) => void;
  onExcluir: (index: number) => void;
  onToggleEmEstoque: (index: number, emEstoque: boolean) => void;
  onSetPadrao: (index: number) => void;
  onAbrirImagens: (
    variacaoProdutoId: string | undefined,
    index: number
  ) => void;
  onSalvarVariacoes: () => void;
  saving: boolean;
}

export default function VariacoesTable({
  variacoes,
  atributos,
  imagensProduto,
  onEditar,
  onExcluir,
  onToggleEmEstoque,
  onSetPadrao,
  onAbrirImagens,
  onSalvarVariacoes,
  saving,
}: VariacoesTableProps) {
  const getImagensVariacao = (
    variacaoProdutoId: string | undefined
  ): IImagemProduto[] => {
    if (!variacaoProdutoId) return [];
    return imagensProduto.filter(
      (img) => img.variacaoProdutoId === variacaoProdutoId
    );
  };

  const rows: VariacaoRowData[] = useMemo(
    () =>
      variacoes.map((v, idx) => ({
        ...v,
        id: v.id ?? `var-${idx}`,
        _index: idx,
        imagens: getImagensVariacao(v.id),
      })),
    [variacoes, imagensProduto]
  );

  const columns: GridColDef<VariacaoRowData>[] = useMemo(
    () => [
      {
        field: "sku",
        headerName: "SKU",
        width: 170,
        flex: 0,
      },
      {
        field: "codigoBarras",
        headerName: "Cód. Barras",
        width: 110,
        flex: 0,
        renderCell: (params) => params.row.codigoBarras || "—",
      },
      {
        field: "atributos",
        headerName: "Atributos",
        width: 150,
        flex: 0,
        renderCell: (params) => {
          const labels = (params.row.atributos || []).map((a) =>
            getLabelAtributoVariacao(a, atributos)
          );
          if (labels.length === 0) return "—";
          return (
            <MDBox display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
              {labels.map((label, i) => (
                <Chip key={i} label={label} size="small" variant="outlined" />
              ))}
            </MDBox>
          );
        },
      },
      {
        field: "preco",
        headerName: "Preço",
        width: 100,
        flex: 0,
        renderCell: (params) => formatCurrency(params.row.preco ?? 0),
      },
      {
        field: "precoOriginal",
        headerName: "Preço Orig.",
        width: 100,
        flex: 0,
        renderCell: (params) =>
          params.row.precoOriginal != null && params.row.precoOriginal > 0
            ? formatCurrency(params.row.precoOriginal)
            : "—",
      },
      {
        field: "precoCusto",
        headerName: "Custo",
        width: 100,
        flex: 0,
        renderCell: (params) =>
          params.row.precoCusto != null &&
          Number.isFinite(Number(params.row.precoCusto))
            ? formatCurrency(params.row.precoCusto)
            : "—",
      },
      {
        field: "estoque",
        headerName: "Estoque",
        width: 70,
        flex: 0,
        type: "number",
      },
      {
        field: "emEstoque",
        headerName: "Em estoque",
        width: 98,
        flex: 0,
        renderCell: (params) => (
          <Switch
            size="small"
            checked={Boolean(params.row.emEstoque)}
            onChange={(_, checked) =>
              onToggleEmEstoque(params.row._index, checked)
            }
          />
        ),
      },
      {
        field: "isPadrao",
        headerName: "Padrão",
        width: 68,
        flex: 0,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Radio
            size="small"
            checked={Boolean(params.row.isPadrao)}
            onChange={() => onSetPadrao(params.row._index)}
            value={params.row._index}
            name="variacao-padrao"
          />
        ),
      },
      {
        field: "imagens",
        headerName: "Imagens",
        width: 190,
        flex: 0,
        renderCell: (params) => {
          const imagens = params.row.imagens ?? [];
          const thumbnails = imagens.slice(0, MAX_THUMBNAILS);
          const restante = imagens.length - MAX_THUMBNAILS;
          return (
            <MDBox display="flex" alignItems="center" gap={0.5}>
              {thumbnails.map((img, i) => (
                <img
                  key={i}
                  src={img.url}
                  alt=""
                  style={{
                    width: THUMB_SIZE,
                    height: THUMB_SIZE,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
              ))}
              {restante > 0 && (
                <Chip
                  label={`+${restante}`}
                  size="medium"
                  sx={{ minWidth: 28 }}
                />
              )}
              <MDButton
                size="medium"
                variant="text"
                color="dark"
                iconOnly
                onClick={() => onAbrirImagens(params.row.id, params.row._index)}
              >
                <Icon fontSize="medium" color="info">
                  add_photo_alternate
                </Icon>
              </MDButton>
            </MDBox>
          );
        },
      },
      {
        field: "acoes",
        headerName: "Ações",
        width: 80,
        flex: 0,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <MDBox display="flex">
            <MDButton
              size="medium"
              variant="text"
              color="dark"
              iconOnly
              onClick={() => onEditar(params.row._index)}
            >
              <Icon fontSize="medium">edit</Icon>
            </MDButton>
            <MDButton
              size="medium"
              variant="text"
              color="error"
              iconOnly
              onClick={() => onExcluir(params.row._index)}
            >
              <Icon fontSize="medium">delete</Icon>
            </MDButton>
          </MDBox>
        ),
      },
    ],
    [
      atributos,
      onEditar,
      onExcluir,
      onToggleEmEstoque,
      onSetPadrao,
      onAbrirImagens,
    ]
  );

  return (
    <MDBox sx={{ width: "100%" }}>
      <LojaDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        getRowClassName={(params) =>
          params.row.isPadrao ? "variacao-row-padrao" : ""
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        slotProps={{
          baseButton: {
            size: "small",
          },
        }}
        sx={{
          "& .variacao-row-padrao": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(144, 202, 249, 0.12)"
                : "rgba(25, 118, 210, 0.09)",
            boxShadow: (theme) =>
              `inset 4px 0 0 0 ${theme.palette.primary.main}`,
          },
          "& .variacao-row-padrao:hover": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(144, 202, 249, 0.18)"
                : "rgba(25, 118, 210, 0.14)",
          },
        }}
      />
      <MDBox mt={2} display="flex" justifyContent="flex-end">
        <MDButton
          variant="gradient"
          color={saving ? "light" : "dark"}
          onClick={onSalvarVariacoes}
          disabled={saving || variacoes.length === 0}
        >
          <Icon sx={{ mr: 0.5 }}>save</Icon>
          {saving ? "Salvando..." : "Salvar variações"}
        </MDButton>
      </MDBox>
    </MDBox>
  );
}
